import React, { useEffect, useRef, useState } from 'react';
import { Input } from "@/components/ui/input";
import { MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GooglePlacesAutocompleteProps {
  types?: string[];
  placeholder?: string;
  onSelect: (data: {
    name: string;
    place_id: string;
    address: string;
    lat: number;
    lng: number;
    country_code?: string;
  }) => void;
  defaultValue?: string;
  className?: string;
  required?: boolean;
  locationBias?: { lat: number; lng: number; radiusMeters?: number };
}

// Maps legacy Places API collection types to the new includedPrimaryTypes values.
const mapTypes = (types: string[]): string[] =>
  types.flatMap((t) => {
    if (t === '(cities)') return ['locality'];
    if (t.startsWith('(')) return []; // drop other legacy collection types
    return [t];
  });

// Inner component — only mounted after google.maps.importLibrary is available.
const PlacesInput: React.FC<GooglePlacesAutocompleteProps> = ({
  types = ['(cities)'],
  placeholder = 'Search area...',
  onSelect,
  defaultValue = '',
  className,
  required = false,
  locationBias,
}) => {
  const [value, setValue] = useState(defaultValue);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Cleanup debounce on unmount
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (input: string) => {
    if (!input || input.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const { AutocompleteSuggestion } = await (window as any).google.maps.importLibrary('places');
      const includedPrimaryTypes = mapTypes(types);
      const request: Record<string, unknown> = { input };
      if (includedPrimaryTypes.length > 0) {
        request.includedPrimaryTypes = includedPrimaryTypes;
      }
      if (locationBias) {
        request.locationBias = {
          center: { lat: locationBias.lat, lng: locationBias.lng },
          radius: locationBias.radiusMeters ?? 50000,
        };
      }
      const { suggestions: results } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      setSuggestions(results ?? []);
    } catch (error) {
      console.error('Error fetching place suggestions:', error);
      setSuggestions([]);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    setShowSuggestions(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(newValue), 300);
  };

  const handleSelect = async (suggestion: any) => {
    const mainText = suggestion.placePrediction.mainText.toString();
    const fullText = suggestion.placePrediction.text.toString();

    setValue(mainText);
    setSuggestions([]);
    setShowSuggestions(false);

    try {
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({ fields: ['location', 'addressComponents', 'formattedAddress', 'displayName'] });

      const lat = place.location.lat();
      const lng = place.location.lng();
      const address = place.formattedAddress || fullText;
      const countryCode = place.addressComponents
        ?.find((c: any) => c.types.includes('country'))?.shortText;

      onSelect({
        name: mainText,
        place_id: suggestion.placePrediction.placeId,
        address,
        lat,
        lng,
        country_code: countryCode,
      });
    } catch (error) {
      console.error('Error getting location details:', error);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Input
          value={value}
          onChange={handleInput}
          placeholder={placeholder}
          className={cn("pr-10", className)}
          required={required}
          onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <MapPin className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>


      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-[102] w-full mt-1 bg-white dark:bg-neutral-900 border-2 border-neutral-200 dark:border-neutral-700 rounded-md shadow-xl max-h-60 overflow-auto">
          {suggestions.map((suggestion) => (
            <li
              key={suggestion.placePrediction.placeId}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(suggestion); }}
              className="px-4 py-3 hover:bg-neutral-100 dark:hover:bg-neutral-800 cursor-pointer transition-colors border-b border-neutral-100 dark:border-neutral-800 last:border-b-0"
            >
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-neutral-500 dark:text-neutral-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                    {suggestion.placePrediction.mainText.toString()}
                  </p>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400 truncate">
                    {suggestion.placePrediction.secondaryText?.toString()}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Outer component — manages script loading and only renders PlacesInput once
// google.maps.importLibrary is available (the entry point for the new Places API).
export const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = (props) => {
  const { className, required = false, placeholder = 'Search area...' } = props;
  const [scriptReady, setScriptReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [pendingValue, setPendingValue] = useState('');

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.error('Google Maps API key not found');
      setHasError(true);
      return;
    }

    // New Places API only needs google.maps.importLibrary — no libraries=places param.
    const isMapsReady = () => !!(window as any).google?.maps?.importLibrary;

    if (isMapsReady()) {
      setScriptReady(true);
      return;
    }

    const timeoutId = setTimeout(() => {
      if (!isMapsReady()) {
        console.error('Google Maps API failed to load within timeout');
        setHasError(true);
      }
    }, 10000);

    // Poll in case the load event fired before this effect attached
    const pollId = setInterval(() => {
      if (isMapsReady()) {
        setScriptReady(true);
        clearTimeout(timeoutId);
        clearInterval(pollId);
      }
    }, 100);

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        console.error('Error loading Google Maps API');
        setHasError(true);
        clearTimeout(timeoutId);
        clearInterval(pollId);
      };
      document.head.appendChild(script);
    }

    return () => { clearTimeout(timeoutId); clearInterval(pollId); };
  }, []);

  if (hasError) {
    return (
      <div className="relative">
        <Input disabled placeholder="Search area..." className={cn("pr-10", className)} required={required} />
        <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">
          Unable to load Google Maps. Please check configuration.
        </p>
      </div>
    );
  }

  if (!scriptReady) {
    return (
      <div className="relative">
        <div className="relative">
          <Input
            value={pendingValue}
            onChange={(e) => setPendingValue(e.target.value)}
            placeholder={placeholder}
            className={cn("pr-10", className)}
            required={required}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  return <PlacesInput {...props} defaultValue={pendingValue || props.defaultValue} />;
};
